/**
 * Partea pura a initializer-ului de telemetrie: numele paginii, redactarea tokenului de
 * colaborator si recunoasterea zgomotului injectat de aplicatia-gazda. Separata de
 * `app-insights.ts` ca sa se poata testa fara SDK si fara `window`.
 */

import type { ITelemetryItem } from "@microsoft/applicationinsights-web";

import { redactCollabToken } from "@/shared/utils/redact-collab-token";

import { pageNameFromUrl } from "./page-name";

function redactStrings(record: Record<string, unknown> | undefined): void {
    if (!record) return;
    for (const key of Object.keys(record)) {
        const value = record[key];
        if (typeof value === "string") {
            record[key] = redactCollabToken(value);
        }
    }
}

/** Scriptul injectat de aplicatia Facebook in WebView-ul ei de Android (masurat 07.10). */
const MARCAJE_INJECTATE = ["Java object is gone", "sendDataToNative"];

/**
 * «Script error.»: tot ce lasa browserul sa se vada dintr-o eroare a unui script de pe ALTA origine.
 * SDK-ul o trimite in doua forme: cu prefixul tipului («ErrorEvent: Script error.») sau, pe calea
 * lui de CORS (`_onerror` -> `_sendCORSException`), cu textul lui lung care incepe cu «Script error:».
 */
const SCRIPT_ERROR = /^(?:[A-Za-z]+: )?Script error(?:\.?$|: The browser's same-origin policy)/;

function siruri(valori: unknown[]): string[] {
    return valori.filter((v): v is string => typeof v === "string");
}

/**
 * Mesajele si liniile de stiva ale unui envelope de exceptie, in AMBELE forme pe care le are la
 * momentul initializer-ului (citite din SDK, `applicationinsights-analytics-js`):
 * - `trackException` / `window.onerror` obisnuit: `baseData.exceptions[]`, fiecare cu `message`,
 *   `stack` si `parsedStack[]` (`fileName`, `assembly`);
 * - calea de CORS: `baseData` e direct exceptia automata (`message`, `stackDetails.obj[]`).
 */
function mesajeSiCadre(baseData: Record<string, unknown>): { mesaje: string[]; cadre: string[] } {
    const mesaje = siruri([baseData.message]);
    const cadre: string[] = [];

    const detalii = baseData.stackDetails as { obj?: unknown } | undefined;
    if (Array.isArray(detalii?.obj)) cadre.push(...siruri(detalii.obj));

    const exceptii = Array.isArray(baseData.exceptions) ? baseData.exceptions : [];
    for (const exceptie of exceptii as Record<string, unknown>[]) {
        if (!exceptie) continue;
        mesaje.push(...siruri([exceptie.message]));
        cadre.push(...siruri([exceptie.stack]));
        const parsate = Array.isArray(exceptie.parsedStack) ? exceptie.parsedStack : [];
        for (const cadru of parsate as Record<string, unknown>[]) {
            if (cadru) cadre.push(...siruri([cadru.fileName, cadru.assembly]));
        }
    }
    return { mesaje, cadre };
}

/**
 * `true` = exceptia nu e a noastra si nu se trimite (initializer-ul intoarce `false`).
 *
 * 🔴 Decizia se ia pe CONTINUTUL erorii, niciodata pe user agent: in WebView-ul Facebook ruleaza si
 * codul nostru, iar erorile lui trebuie sa ramana vizibile. Doua tipare, masurate 07.10 (179 din 187
 * de exceptii de browser in 14 zile):
 * - mesajul contine «Java object is gone» sau `sendDataToNative` — puntea nativa a aplicatiei-gazda;
 * - «Script error.» FARA niciun cadru de stiva de pe originea noastra. Scripturile noastre sunt
 *   servite de pe aceeasi origine, deci o eroare a lor vine cu mesaj si stiva, nu asa; cadrul propriu
 *   e plasa pentru ziua in care un script de-al nostru ajunge pe alt domeniu.
 *
 * @param origine originea paginii (`location.origin`); parametru ca functia sa ramana pura
 */
export function esteZgomotTerta(
    envelope: ITelemetryItem,
    origine: string = globalThis.location?.origin ?? "",
): boolean {
    if (envelope.baseType !== "ExceptionData" || !envelope.baseData) return false;

    const { mesaje, cadre } = mesajeSiCadre(envelope.baseData);
    return esteZgomotTertaDupaContinut(mesaje, cadre, origine);
}

/**
 * Regula din `esteZgomotTerta`, pe mesaje si cadre deja extrase: aceeasi decizie pentru orice SDK
 * (App Insights aici, PostHog in `posthog-sanitize.ts`), ca cele doua surse sa nu poata diverge.
 */
export function esteZgomotTertaDupaContinut(mesaje: string[], cadre: string[], origine: string): boolean {
    if (mesaje.some((mesaj) => MARCAJE_INJECTATE.some((marcaj) => mesaj.includes(marcaj)))) {
        return true;
    }

    if (!mesaje.some((mesaj) => SCRIPT_ERROR.test(mesaj.trim()))) return false;
    const areCadruPropriu = origine !== "" && cadre.some((cadru) => cadru.includes(origine));
    return !areCadruPropriu;
}

export function sanitizeEnvelope(envelope: ITelemetryItem): void {
    // Tokenul de colaborator e o credentiala bearer, dar sta in path, nu in header. Trei cai il pot
    // scurge, iar `redactCollabToken` trebuie sa recunoasca ambele forme de URL
    // (vezi comentariul din shared/utils/redact-collab-token.ts, fix round 2 - E1):
    // - apelurile /api/collab/** pleaca spre Azure ca dependency telemetry, cu tokenul in
    //   name/target/data (RemoteDependencyData);
    // - pagina publica a colaboratorului (`.../activitate/<token>`) pleaca ca PageView, cu tokenul
    //   in name/uri (IPageViewData);
    // - orice envelope emis pe pagina aceea poarta calea paginii in `tags["ai.operation.name"]`
    //   (masurat 2026-09-21 pe AJAX: operation_Name = `/activitate/<token>`).
    // Iterarea e generica pe CAMPURI, nu presupune care schema a produs envelope-ul - dar nu
    // inlocuieste patternul din `redactCollabToken`: o forma de URL pe care patternul n-o
    // recunoaste nu se redacteaza nicaieri.
    redactStrings(envelope.baseData);
    redactStrings(envelope.tags);

    // Dupa redactare, ca `uri` sa fie deja curat; `pageNameFromUrl` inlocuieste oricum tokenul.
    if (envelope.baseType === "PageviewData" && envelope.baseData) {
        const name = pageNameFromUrl(envelope.baseData.uri as string | undefined);
        if (name) envelope.baseData.name = name;
    }
}
